/*
 * Copyright (c) 2022. American Express Company
 */

// theirs
import React from 'react';
import _ from 'lodash';
import { LocalDate } from '@js-joda/core';

// ours
import H from 'history';
import { CashFlowModel } from 'src/types/cash-flow.model';
import { CardModel } from 'src/types/card.model';
import CardService from './card.service';
import { MonteCarloEstimate } from 'src/types/monte-carlo.enum';
import CashFlowService from './cashflow.service';
import { BaseAssetModel, BaseDebtModel } from 'src/types/asset-debt.model';
import { goalIcons } from 'src/bw/goal/Goal';
import { AdjustmentModel } from 'src/types/adjusment.model';
import LiteralsService from '../../src/services/literals/LiteralsService';
import NavigationService from 'src/services/navigation.service';

// styles
import styles from './report.service.module.scss';
import colours from 'src/styles/variables.module.scss';
import { BaseExpenseModel, BaseIncomeModel } from 'src/types/income-expense.model';

export class PlotPoint {
    income: number;
    expense: number;
    incomeMax?: number;
    expenseMax?: number;
    startDate: LocalDate;
    endDate: LocalDate;
}

export class ReportBar {
    uuid: string;
    name: string;
    amount: number;
    percentage: number;
}

export class ReportBarPackage {
    incomes: ReportBar[];
    expenses: ReportBar[];
}

export default class ReportService {
    public static getLiquidAndIlliquidAssets(
        cashFlowDetails: any,
        monteCarlo: MonteCarloEstimate = MonteCarloEstimate.AVERAGE
    ): number[] {
        try {
            const assets: BaseAssetModel[] = cashFlowDetails.assets.bases;
            const liquidAssets: BaseAssetModel[] = _.filter(assets, { liquid: true });
            let liquidAssetsTotal = 0;
            const illiquidAssets: BaseAssetModel[] = _.filter(assets, { liquid: false });
            let illiquidAssetsTotal = 0;

            switch (monteCarlo) {
                case MonteCarloEstimate.PESSIMISTIC:
                    liquidAssetsTotal = _.sumBy(liquidAssets, 'amountLow');
                    illiquidAssetsTotal = _.sumBy(illiquidAssets, 'amountLow');
                    break;
                case MonteCarloEstimate.AVERAGE:
                    liquidAssetsTotal = _.sumBy(liquidAssets, 'amount');
                    illiquidAssetsTotal = _.sumBy(illiquidAssets, 'amount');
                    break;
                case MonteCarloEstimate.OPTIMISTIC:
                    liquidAssetsTotal = _.sumBy(liquidAssets, 'amountHigh');
                    illiquidAssetsTotal = _.sumBy(illiquidAssets, 'amountHigh');
                    break;
            }
            return [liquidAssetsTotal, illiquidAssetsTotal];
        } catch (e) {
            console.log(e);
            return [];
        }
    }

    public static getAsset(monteCarlo: MonteCarloEstimate, cashFlowDetails: any): number {
        const assets: BaseAssetModel[] = cashFlowDetails.assets.bases;

        switch (monteCarlo) {
            case MonteCarloEstimate.PESSIMISTIC:
                return _.sumBy(assets, 'amountLow');
            case MonteCarloEstimate.OPTIMISTIC:
                return _.sumBy(assets, 'amountHigh');
            default:
                return _.sumBy(assets, 'amount');
        }
    }

    public static updateColorMapForTitle(colorMap: Map<string, string>, title: string): Map<string, string> {
        const barColors: string[] = [
            colours.aqua400,
            colours.aqua500,
            colours.aqua600,
            colours.brightBlue400,
            colours.brightBlue500,
            colours.brightBlue600,
            colours.deepBlue400,
            colours.deepBlue500,
            colours.deepBlue600,
            colours.green400,
            colours.green500,
            colours.green600,
            colours.magenta400,
            colours.magenta500,
            colours.magenta600,
            colours.orange400,
            colours.orange500,
            colours.orange600
        ];

        if (!colorMap.has(title)) {
            let hashCode: number = barColors.length - 1;
            for (let i = 0; i < title.length; i++) {
                hashCode = title.charCodeAt(i) + ((hashCode << 5) - hashCode);
            }
            hashCode %= barColors.length;
            while (Array.from(colorMap.values()).includes(barColors.at(hashCode))) {
                hashCode = (hashCode + 1) % barColors.length;
            }
            colorMap.set(title, barColors.at(hashCode));
        }

        return colorMap;
    }

    public static getIncomeAndExpenseStats(drawerProps: any): number[] {
        try {
            return [drawerProps.cashFlowDetails.incomes.amount, drawerProps.expenseTotal, drawerProps.delta];
        } catch (e) {
            console.log(e);
            return [];
        }
    }

    public static getAssetAndDebtStats(monteCarlo: MonteCarloEstimate, drawerProps: any): number[] {
        try {
            const asset: number = this.getAsset(monteCarlo, drawerProps.cashFlowDetails);
            const assetAdjustment: number = _.sumBy(drawerProps.cashFlowDetails.assets.adjustments, 'amount');
            const afterAsset: number = asset + assetAdjustment;
            const afterDebt: number = drawerProps.cashFlowDetails.debts.amount;
            const netWorth: number = afterAsset - afterDebt + Math.max(drawerProps.freeCash, 0);

            return [afterAsset, afterDebt, netWorth];
        } catch (e) {
            console.log(e);
            return [];
        }
    }

    public static getNetworthStats(monteCarlo: MonteCarloEstimate, drawerProps: any): number {
        try {
            const asset: number = this.getAsset(monteCarlo, drawerProps.cashFlowDetails);
            const assetAdjustment: number = _.sumBy(drawerProps.cashFlowDetails.assets.adjustments, 'amount');
            const afterAsset: number = asset + assetAdjustment;
            const afterDebt: number = drawerProps.cashFlowDetails.debts.amount;
            const offset: number = CashFlowService.getCashFlowIndexWithDate(
                drawerProps.user.cashFlow,
                drawerProps.startDate
            );
            const freecash: number = drawerProps.user.cashFlow[offset].freeCash;

            return afterAsset - afterDebt + Math.max(freecash, 0);
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    public static getPlotPoints(cashFlows: CashFlowModel[], sampleStart: LocalDate, sampleEnd: LocalDate): PlotPoint[] {
        if (_.isEmpty(cashFlows)) return;

        cashFlows = cashFlows.filter((cf: CashFlowModel) => {
            return (
                (cf.endDate.isBefore(sampleEnd) || cf.endDate.isEqual(sampleEnd)) &&
                (cf.startDate.isAfter(sampleStart) || cf.startDate.isEqual(sampleStart))
            );
        });

        const incomeMax: number = _.maxBy(cashFlows, 'income').income;
        const expenseMax: number = _.minBy(cashFlows, 'expense').expense;
        const incomeExpenseMax = Math.max(incomeMax, -expenseMax);

        return cashFlows.map((cf: CashFlowModel, index: number): PlotPoint => {
            let plotPoint: PlotPoint = {
                income: cf.income / incomeExpenseMax,
                expense: cf.expense / -incomeExpenseMax,
                startDate: cf.startDate,
                endDate: cf.endDate
            };

            if (index === 0) {
                plotPoint = {
                    ...plotPoint,
                    incomeMax,
                    expenseMax
                };
            }

            return plotPoint;
        });
    }

    public static getCardDetails(
        adjustments: AdjustmentModel[],
        value: AdjustmentModel | BaseIncomeModel | BaseAssetModel | BaseDebtModel | BaseExpenseModel,
        cards: CardModel[],
        history: H.History
    ): [JSX.Element, string, () => void] {
        const relatedAdjustment: AdjustmentModel = adjustments?.find((adj: AdjustmentModel) => {
            return value.name === adj.name;
        });

        let relatedCard: CardModel;
        // TODO: IDs mysteriously missing from payload. Bug in Phant? investigate. Best fit workaround for now
        if (relatedAdjustment) relatedCard = _.find(cards, (card: CardModel) => card.id === relatedAdjustment.cardId);

        let cardImage: JSX.Element;
        let cardName: string;
        let onClick: () => void;
        if (relatedCard && !relatedCard.description.toLowerCase().includes('ripple effects')) {
            cardImage = this.getCardImage(relatedCard);
            cardName = relatedCard.nickname || LiteralsService.getLiteral('Goals.' + relatedCard.description);
            onClick = () => {
                void NavigationService.goTo(history, `/card/${relatedCard.id}`, `${cardName} Drawer`);
            };
        }
        return [cardImage, cardName, onClick];
    }

    public static getCardImage(card: CardModel): JSX.Element {
        return card.hash ? (
            <div className={styles.cardImageContainer}>
                <img className={styles.cardImage} src={CardService.getPhotoUrl(card)} alt={card.description} />
            </div>
        ) : (
            <div className={styles.cardImageContainer}>{goalIcons[card.description]}</div>
        );
    }
}
